import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { baseUrl } from "../services/BaseURL";

/**
 * BrandingContext — fetches Settings → Company Branding once on app boot
 * and exposes it to the whole tree. Applies primary/accent colours as
 * CSS variables on <html> so plain CSS + Tailwind arbitrary values can
 * reference them (`bg-[var(--brand-primary)]`).
 *
 * Defaults are preserved until the server responds, so the login screen
 * never renders a naked/white flash.
 */

const DEFAULTS = {
  companyName:   "Kenshin Systems",
  companyPhone:  "",
  companyAddress:"",
  contactEmail:  "",
  primaryColor:  "#2563eb",
  accentColor:   "#10b981",
  currency:      "INR",
  dateFormat:    "DD-MM-YYYY",
  timezone:      "Asia/Kolkata",
  logo:          "",
  logoUrl:       `${process.env.PUBLIC_URL || ""}/logo.png`,
  favicon:       "",
  faviconUrl:    `${process.env.PUBLIC_URL || ""}/favicon.ico`,
};

const BrandingContext = createContext(DEFAULTS);
const BRANDING_CACHE_KEY = "prosale:branding";

function assetUrl(url) {
  if (!url) return null;
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  if (url.startsWith("/uploads/")) return `${baseUrl}${url}`;
  if (url.startsWith("/")) return url;
  return `${baseUrl}/uploads/${url}`;
}

function normalizeBranding(raw) {
  const merged = { ...DEFAULTS, ...(raw || {}) };
  return {
    ...merged,
    logoUrl: assetUrl(merged.logoUrl || merged.logo) || DEFAULTS.logoUrl,
    faviconUrl: assetUrl(merged.faviconUrl || merged.favicon) || DEFAULTS.faviconUrl,
  };
}

function cachedBranding() {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = localStorage.getItem(BRANDING_CACHE_KEY);
    return raw ? normalizeBranding(JSON.parse(raw)) : DEFAULTS;
  } catch (_) {
    return DEFAULTS;
  }
}

function applyCssVars(b) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (b.primaryColor) root.style.setProperty("--brand-primary", b.primaryColor);
  if (b.accentColor)  root.style.setProperty("--brand-accent",  b.accentColor);
  if (b.companyName)  document.title = b.companyName;
  const iconUrl = b.faviconUrl || b.logoUrl;
  if (iconUrl) {
    const link =
      document.querySelector("link[rel~='icon']") ||
      (() => {
        const l = document.createElement("link");
        l.rel = "icon";
        document.head.appendChild(l);
        return l;
      })();
    link.href = `${iconUrl}${iconUrl.includes("?") ? "&" : "?"}v=${Date.now()}`;
  }
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(cachedBranding);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const initialBranding = cachedBranding();
    applyCssVars(initialBranding);
    axios
      .get(`${baseUrl}/branding`, { timeout: 5000 })
      .then((res) => {
        if (cancelled) return;
        const merged = normalizeBranding(res.data);
        setBranding(merged);
        applyCssVars(merged);
        try {
          localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(merged));
        } catch (_) {}
      })
      .catch(() => {
        // Silently keep defaults — backend might not be reachable yet.
        applyCssVars(initialBranding);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => ({ ...branding, loaded }), [branding, loaded]);
  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
